import type { LandingPageConfig } from '@provision/landing-page-utils';
import { COPY } from './copy';
import type { LandingPageCtaTarget, LandingPageProps } from './types';

export interface CtaTarget {
  label: string;
  /** Set in consultation mode with a scheduling URL; the button is a link then. */
  href?: string;
  onClick?: () => void;
  /** The host is still acting on this button's click. */
  pending?: boolean;
  /** Nothing to act on, such as a call-for-pricing plan with no checkout. */
  disabled?: boolean;
}

interface ResolveCtaParams {
  settings: LandingPageConfig['settings'];
  onCta: LandingPageProps['onCta'];
  pendingCta: LandingPageProps['pendingCta'];
  blockId: string;
  pricePointId?: string;
  /** Defaults to the mode's label; the closing CTA carries its own. */
  label?: string;
  /** A plan priced by phone: there is no checkout to start, so only a scheduling link survives. */
  callForPricing?: boolean;
}

/** One rule for every call to action: a scheduling link when there is one, the host's callback otherwise. */
export function resolveCta({
  settings,
  onCta,
  pendingCta,
  blockId,
  pricePointId,
  label,
  callForPricing,
}: ResolveCtaParams): CtaTarget {
  const resolvedLabel = label ?? COPY.ctaLabel[settings.cta.mode];
  const url = settings.cta.mode === 'consultation' ? settings.cta.consultationUrl?.trim() : '';

  if (url) return { label: resolvedLabel, href: url };

  if (callForPricing) return { label: COPY.callForPricing, disabled: true };

  if (isSameCta(pendingCta, { blockId, pricePointId })) {
    return { label: resolvedLabel, pending: true };
  }

  return {
    label: resolvedLabel,
    onClick: onCta ? () => onCta({ blockId, pricePointId }) : undefined,
  };
}

function isSameCta(a: LandingPageCtaTarget | undefined, b: LandingPageCtaTarget): boolean {
  return a?.blockId === b.blockId && a.pricePointId === b.pricePointId;
}
